/**
 * Beleg erfassen.
 *
 * Die Maske ist nach dem Beleg gebaut, der vor einem liegt: Was oben auf
 * dem Kassenzettel steht, steht oben im Formular. Reihenfolge und
 * Vorbelegung sparen bei fünfzig Belegen im Monat mehr Zeit als jede
 * Automatik.
 *
 * Das Zahlungsdatum ist bewusst nicht vorbelegt. Es entscheidet über das
 * Geschäftsjahr (Zuflussprinzip) und darf nicht aus Bequemlichkeit auf
 * „heute" stehen, wenn die Rechnung erst nächsten Monat abgeht.
 */
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Ankreuzfeld,
  Auswahlfeld,
  Betragsfeld,
  Textbereich,
  Textfeld,
} from '../bausteine/Feld';
import { KontaktWahl } from '../bausteine/KontaktWahl';
import { Seite } from '../bausteine/Layout';
import { Fehlermeldung, Meldung } from '../bausteine/Meldung';
import { useAbruf, useVorgang } from '../lib/abruf';
import { api } from '../lib/api';
import { heute } from '../lib/format';
import { alsCent, formatiereCent, istBetrag, nettoAusBrutto, steuerAusBrutto } from '../lib/geld';
import type { Beleg, Kategorie, KontaktListe, Richtung } from '../lib/typen';

const STEUERSAETZE = [
  { wert: '0', text: '0 % (keine Umsatzsteuer)' },
  { wert: '7', text: '7 %' },
  { wert: '19', text: '19 %' },
] as const;

export function BelegNeu(): ReactNode {
  const navigiere = useNavigate();
  const vorgang = useVorgang();

  const kategorien = useAbruf<Kategorie[]>('/api/kategorien');
  const kontakte = useAbruf<KontaktListe>('/api/kontakte?proSeite=200');

  const [richtung, setzeRichtung] = useState<Richtung>('ausgabe');
  const [belegDatum, setzeBelegDatum] = useState(heute());
  const [zahlungsdatum, setzeZahlungsdatum] = useState('');
  const [leistungsdatum, setzeLeistungsdatum] = useState('');
  const [betrag, setzeBetrag] = useState('');
  const [steuersatz, setzeSteuersatz] = useState('19');
  const [kategorieId, setzeKategorieId] = useState('');
  const [kontaktId, setzeKontaktId] = useState('');
  const [bezeichnung, setzeBezeichnung] = useState('');
  const [notiz, setzeNotiz] = useState('');
  const [privatanteil, setzePrivatanteil] = useState('0');
  const [datei, setzeDatei] = useState<File | null>(null);
  const [gleichBuchen, setzeGleichBuchen] = useState(false);

  // Die Kategorien der Gegenrichtung wären zwar wählbar, aber falsch: Eine
  // Einnahme in einer Ausgabenkategorie verfälscht jede Auswertung.
  const passende = (kategorien.daten ?? []).filter(
    (k) => k.art === richtung || k.art === 'privat' || k.art === 'neutral',
  );

  const bruttoCent = istBetrag(betrag) ? alsCent(betrag) : 0;
  const satz = Number.parseFloat(steuersatz);
  const gewaehlteKategorie = passende.find((k) => k.id === kategorieId);
  const abzug = gewaehlteKategorie?.abzugProzent
    ? Number.parseFloat(gewaehlteKategorie.abzugProzent)
    : 100;
  const privat = Number.parseFloat(privatanteil) || 0;

  // Was am Ende in der EÜR ankommt — dieselbe Formel wie in
  // api/src/domain/euer.ts. Als Vorschau, gerechnet wird auf dem Server.
  const wirksamCent = Math.round((bruttoCent * ((100 - privat) / 100) * abzug) / 100);

  const fertig = bezeichnung.trim() !== '' && istBetrag(betrag) && bruttoCent > 0;

  async function speichern(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!fertig) return;

    await vorgang.fuehreAus(async () => {
      const neu = await api.senden<Beleg>('/api/belege', {
        richtung,
        belegDatum,
        ...(leistungsdatum ? { leistungsdatum } : {}),
        ...(zahlungsdatum ? { zahlungsdatum } : {}),
        betragBrutto: betrag,
        steuersatz: satz,
        ...(kategorieId ? { kategorieId } : {}),
        ...(kontaktId ? { kontaktId } : {}),
        bezeichnung: bezeichnung.trim(),
        ...(notiz.trim() ? { notiz: notiz.trim() } : {}),
        privatanteilProzent: privat,
      });

      // Die Datei erst nach dem Beleg: Sie braucht seine Kennung. Scheitert
      // der Anhang, ist der Beleg trotzdem da — deshalb wird der Fehler
      // gemeldet, aber nicht der ganze Vorgang verworfen.
      if (datei) {
        await api.sendeDatei(`/api/belege/${neu.id}/dateien`, datei);
      }

      if (gleichBuchen && zahlungsdatum) {
        await api.senden(`/api/belege/${neu.id}/buchen`);
      }

      navigiere(`/belege/${neu.id}`);
      return neu;
    });
  }

  return (
    <Seite titel="Beleg erfassen" untertitel={richtung === 'ausgabe' ? 'Ausgabe' : 'Einnahme'}>
      <form className="stapel" style={{ maxWidth: '860px' }} onSubmit={(e) => void speichern(e)}>
        <Fehlermeldung text={vorgang.fehler} />

        <div className="karte">
          <div className="karte-kopf">
            <h2>Der Beleg</h2>
          </div>
          <div className="karte-koerper formularraster">
            <Auswahlfeld<Richtung>
              beschriftung="Art"
              wert={richtung}
              setzeWert={(w) => {
                setzeRichtung(w);
                // Die gewählte Kategorie passt nach dem Wechsel meist
                // nicht mehr — lieber leeren als falsch stehen lassen.
                setzeKategorieId('');
                setzeSteuersatz(w === 'einnahme' ? '0' : '19');
              }}
              optionen={[
                { wert: 'ausgabe', text: 'Ausgabe' },
                { wert: 'einnahme', text: 'Einnahme' },
              ]}
              pflicht
            />

            <Textfeld
              beschriftung="Belegdatum"
              art="date"
              wert={belegDatum}
              setzeWert={setzeBelegDatum}
              pflicht
              hinweis="Datum auf dem Beleg"
            />

            <Textfeld
              beschriftung="Zahlungsdatum"
              art="date"
              wert={zahlungsdatum}
              setzeWert={setzeZahlungsdatum}
              hinweis="Entscheidet über das Geschäftsjahr"
            />

            <Textfeld
              beschriftung="Bezeichnung"
              wert={bezeichnung}
              setzeWert={setzeBezeichnung}
              pflicht
              platzhalter="Hosting Februar, Bahnfahrt Stuttgart …"
              maxLaenge={300}
              breit
            />

            <Betragsfeld
              beschriftung="Betrag brutto"
              wert={betrag}
              setzeWert={setzeBetrag}
              pflicht
            />

            <Auswahlfeld
              beschriftung="Steuersatz"
              wert={steuersatz}
              setzeWert={setzeSteuersatz}
              optionen={STEUERSAETZE}
              hinweis={
                richtung === 'ausgabe'
                  ? 'Nur zur Dokumentation — als Kleinunternehmer kein Vorsteuerabzug.'
                  : 'Als Kleinunternehmer nach § 19 UStG: 0 %.'
              }
            />

            <Auswahlfeld
              beschriftung="Kategorie"
              wert={kategorieId}
              setzeWert={setzeKategorieId}
              leerText="— noch offen —"
              optionen={passende.map((k) => ({
                wert: k.id,
                text:
                  k.abzugProzent && Number.parseFloat(k.abzugProzent) < 100
                    ? `${k.bezeichnung} (${Number.parseFloat(k.abzugProzent)} % abziehbar)`
                    : k.bezeichnung,
              }))}
              hinweis="Ohne Kategorie zählt der Beleg in keiner EÜR-Position."
            />

            <KontaktWahl
              beschriftung={richtung === 'ausgabe' ? 'Lieferant' : 'Kunde'}
              wert={kontaktId}
              setzeWert={setzeKontaktId}
              kontakte={kontakte.daten?.kontakte ?? []}
              typ={richtung === 'ausgabe' ? 'lieferant' : 'kunde'}
              leerText="— keiner —"
              neuGeladen={kontakte.neuLaden}
            />

            <Textfeld
              beschriftung="Leistungsdatum"
              art="date"
              wert={leistungsdatum}
              setzeWert={setzeLeistungsdatum}
              hinweis="Nur wenn es vom Belegdatum abweicht"
            />

            <Textfeld
              beschriftung="Privatanteil"
              art="number"
              wert={privatanteil}
              setzeWert={setzePrivatanteil}
              klasse="zahl"
              hinweis="in Prozent, etwa 30 beim Telefon"
            />

            <Textbereich
              beschriftung="Notiz"
              wert={notiz}
              setzeWert={setzeNotiz}
              zeilen={2}
              breit
              hinweis="Wofür war das? In zwei Jahren weiß es niemand mehr."
            />
          </div>
        </div>

        <div className="karte">
          <div className="karte-kopf">
            <h2>Belegbild</h2>
            <span className="klein still">Foto oder PDF</span>
          </div>
          <div className="karte-koerper stapel">
            <input
              type="file"
              accept="image/jpeg,image/png,image/heic,application/pdf"
              onChange={(e) => setzeDatei(e.target.files?.[0] ?? null)}
            />
            <p className="klein still" style={{ margin: 0 }}>
              Der Beleg wird mit SHA-256-Prüfsumme abgelegt. Weitere Seiten
              lassen sich später anhängen. Ohne Bild ist der Beleg gültig,
              aber bei einer Prüfung nicht nachweisbar.
            </p>
          </div>
        </div>

        {bruttoCent > 0 ? (
          <div className="karte">
            <div className="karte-kopf">
              <h2>Vorschau</h2>
            </div>
            <div className="karte-koerper">
              <dl className="paare">
                <dt>Brutto</dt>
                <dd className="zahl">{formatiereCent(bruttoCent)}</dd>
                {satz > 0 ? (
                  <>
                    <dt>Netto</dt>
                    <dd className="zahl">{formatiereCent(nettoAusBrutto(bruttoCent, satz))}</dd>
                    <dt>enthaltene Steuer</dt>
                    <dd className="zahl">{formatiereCent(steuerAusBrutto(bruttoCent, satz))}</dd>
                  </>
                ) : null}
                {wirksamCent !== bruttoCent ? (
                  <>
                    <dt>wirksam in der EÜR</dt>
                    <dd className="zahl">
                      {formatiereCent(wirksamCent)}
                      <span className="klein still">
                        {privat > 0 ? ` · ${privat} % privat` : ''}
                        {abzug < 100 ? ` · ${abzug} % abziehbar` : ''}
                      </span>
                    </dd>
                  </>
                ) : null}
              </dl>
            </div>
          </div>
        ) : null}

        {zahlungsdatum ? (
          <Ankreuzfeld
            beschriftung="Gleich buchen — Nummer vergeben und für die EÜR wirksam machen"
            wert={gleichBuchen}
            setzeWert={setzeGleichBuchen}
          />
        ) : (
          <Meldung ton="info">
            Ohne Zahlungsdatum bleibt der Beleg ein offener Posten. Buchen
            lässt er sich erst, wenn die Zahlung feststeht — für die EÜR
            zählt der Zufluss.
          </Meldung>
        )}

        <div className="reihe">
          <button type="submit" className="knopf haupt" disabled={!fertig || vorgang.laeuft}>
            {vorgang.laeuft ? 'Wird gespeichert …' : 'Beleg speichern'}
          </button>
          <button
            type="button"
            className="knopf"
            onClick={() => navigiere('/belege')}
            disabled={vorgang.laeuft}
          >
            Abbrechen
          </button>
        </div>
      </form>
    </Seite>
  );
}
